import React, { useMemo, useRef, useState } from 'react';
import { Customer, QuoteStatus } from '../../types';
import { extractQuoteDocument } from '../../services/geminiService';
import { parseQuoteDocument, QuoteImportChoice } from '../../services/quoteDocumentImport';
import { customerSimilarity } from '../../services/crmMatching';
import { Button } from '../Button';
import {
  X, Upload, Loader2, AlertTriangle, FileText, CheckCircle2, Info,
} from 'lucide-react';

/**
 * Ein FERTIGES Angebot (eigener Altbeleg oder eines aus einem Vorsystem) als
 * PDF einlesen und daraus einen Angebotsdatensatz anlegen. Damit taucht der
 * Altbestand in der Angebots-Statistik auf und kann ueber den normalen Weg
 * ("Auftrag anlegen") zum Projekt werden.
 *
 * Abgrenzung zum TenderImportModal: dort ist das Dokument die AUSSCHREIBUNG
 * des Auftraggebers und Preise werden bewusst nicht gelesen.
 */

/** Die im Review bestaetigte Auswahl plus der zugeordnete Kunde. */
export interface QuoteDocumentImportResult extends QuoteImportChoice {
  customerId: string;
}

interface Props {
  customers: Customer[];
  /** Steuersatz, wenn der Beleg keinen ausweist. */
  defaultTaxRate: number;
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  onImport: (result: QuoteDocumentImportResult) => void;
}

const MAX_BYTES = 18 * 1024 * 1024;
const ACCEPT = 'application/pdf,image/*';

const SUPPORTED = (file: File): boolean =>
  file.type === 'application/pdf' || file.type.startsWith('image/');

const inputClass =
  'w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all';
const labelClass = 'text-[11px] font-bold text-slate-500 uppercase tracking-widest';

const eur = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error || new Error('Datei konnte nicht gelesen werden.'));
    reader.readAsDataURL(file);
  });

/**
 * Status eines eingelesenen Altbelegs. Bewusst ohne Vorbelegung auf
 * "angenommen": ob der Auftrag kam, weiss nur der Nutzer, und ein falsch
 * gesetztes "angenommen" verfaelscht direkt die Erfolgsquote.
 */
const STATUS_OPTIONS: { value: QuoteStatus; label: string; hint: string }[] = [
  { value: 'sent', label: 'Versendet — Entscheidung offen', hint: 'Zählt in der offenen Pipeline.' },
  { value: 'accepted', label: 'Angenommen', hint: 'Zählt als gewonnenes Volumen.' },
  { value: 'rejected', label: 'Abgelehnt', hint: 'Zählt als entschieden, aber nicht gewonnen.' },
  { value: 'draft', label: 'Entwurf', hint: 'Zählt nur bei den Entwürfen mit.' },
];

export const QuoteDocumentImportModal: React.FC<Props> = ({
  customers, defaultTaxRate, busy, error: outerError, onClose, onImport,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);

  const [phase, setPhase] = useState<'select' | 'loading' | 'review' | 'error'>('select');
  const [error, setError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState<QuoteImportChoice['parsed'] | null>(null);

  // Im Review korrigierbare Felder.
  const [customerId, setCustomerId] = useState('');
  const [status, setStatus] = useState<QuoteStatus>('sent');
  const [title, setTitle] = useState('');
  const [quoteDate, setQuoteDate] = useState('');
  const [validUntil, setValidUntil] = useState('');
  // Entscheidungsdatum: steht nicht im Angebotsbeleg, ist aber der Anker jeder
  // Fluss-Kennzahl (Erfolgsquote, gewonnenes Volumen). Vorbelegt mit dem
  // Angebotsdatum, damit der Import nicht im laufenden Monat landet.
  const [decisionDate, setDecisionDate] = useState('');
  const [taxRate, setTaxRate] = useState(defaultTaxRate);

  /** Bester Namenstreffer im Kundenstamm — nur ein Vorschlag, nie gesetzt ohne Anzeige. */
  const suggestCustomer = (name: string, street: string, zip: string, city: string): string => {
    if (!name) return '';
    const address = [street, [zip, city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    let bestId = '';
    let best = 0;
    for (const c of customers) {
      const score = customerSimilarity({ name, address }, { name: c.name, address: c.address });
      if (score > best) { best = score; bestId = c.id; }
    }
    // Unter 0.6 ist es Rauschen — dann lieber leer lassen und fragen.
    return best >= 0.6 ? bestId : '';
  };

  const runExtraction = async (file: File) => {
    setPhase('loading');
    setError(null);
    setTruncated(false);
    setFileName(file.name);
    try {
      const base64 = await fileToBase64(file);
      const { data, truncated: cut } = await extractQuoteDocument(base64, file.type);
      const result = parseQuoteDocument(data);
      if (!result.items.length) {
        throw new Error(cut
          ? 'Die KI-Antwort wurde abgeschnitten, bevor eine Position lesbar war. Bitte den Beleg aufteilen.'
          : 'Keine Positionen erkannt. Ist das Dokument ein Angebot?');
      }
      setParsed(result);
      setTruncated(cut);
      setTitle(result.header.title);
      setQuoteDate(result.header.quoteDate);
      setValidUntil(result.header.validUntil);
      setDecisionDate(result.header.quoteDate);
      setTaxRate(result.header.taxRate || defaultTaxRate);
      setCustomerId(suggestCustomer(
        result.header.customerName,
        result.header.siteStreet,
        result.header.siteZip,
        result.header.siteCity,
      ));
      setPhase('review');
    } catch (e) {
      setError((e as Error)?.message || 'Die KI-Analyse ist fehlgeschlagen.');
      setPhase('error');
    }
  };

  const handleFile = (file?: File | null) => {
    if (!file) return;
    if (!SUPPORTED(file)) {
      setError('Dieses Format kann nicht gelesen werden. Unterstützt werden PDF und Bilder (Scan/Foto).');
      setPhase('error');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`Datei ist zu gross (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximal 18 MB.`);
      setPhase('error');
      return;
    }
    runExtraction(file);
  };

  const suggestedName = parsed?.header.customerName || '';
  const canImport = !!customerId && !!parsed && !busy;

  const deviationHint = useMemo(() => {
    if (!parsed || !parsed.netDeviation) return null;
    const missing = parsed.netDeviation < 0;
    return {
      missing,
      text: missing
        ? `Der Beleg weist ${eur(parsed.header.statedNet)} € netto aus, die erkannten Positionen ergeben nur `
          + `${eur(parsed.computedNet)} € — es fehlen ${eur(Math.abs(parsed.netDeviation))} €. `
          + 'Wahrscheinlich wurde eine Position nicht erkannt.'
        : `Die erkannten Positionen ergeben ${eur(parsed.computedNet)} € netto, der Beleg weist nur `
          + `${eur(parsed.header.statedNet)} € aus (${eur(parsed.netDeviation)} € zu viel). `
          + 'Wahrscheinlich wurde eine Zeile doppelt gelesen.',
    };
  }, [parsed]);

  const handleImport = () => {
    if (!parsed || !customerId) return;
    onImport({
      parsed,
      customerId,
      status,
      title,
      quoteDate,
      validUntil,
      decisionDate: status === 'accepted' || status === 'rejected' ? decisionDate : '',
      taxRate,
      fileName,
    });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onMouseDown={onClose}>
      <div
        className="w-full max-w-3xl max-h-[90vh] flex flex-col bg-white rounded-3xl shadow-2xl overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Kopf */}
        <div className="flex items-start justify-between gap-3 px-6 py-5 border-b border-slate-100">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-blue-50 rounded-xl text-blue-600 shrink-0"><FileText className="w-5 h-5" /></div>
            <div>
              <h2 className="text-base font-bold text-slate-800">Angebot einlesen</h2>
              <p className="text-xs text-slate-500 mt-0.5 max-w-xl">
                Ein fertiges Angebot (Altbeleg oder aus einem Vorsystem) als PDF übernehmen. Es entsteht ein
                Angebot mit eigener Nummer, das in der Statistik mitzählt und über „Auftrag anlegen“ zum Projekt wird.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all shrink-0" title="Schließen">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Inhalt */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {(phase === 'select' || phase === 'error') && (
            <div className="space-y-4">
              {phase === 'error' && error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <button
                onClick={() => fileRef.current?.click()}
                className="w-full flex flex-col items-center justify-center gap-3 py-12 border-2 border-dashed border-slate-200 rounded-2xl text-slate-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50/40 transition-all"
              >
                <Upload className="w-8 h-8" />
                <span className="text-sm font-semibold">Angebot wählen (PDF oder Bild)</span>
                <span className="text-xs">max. 18 MB</span>
              </button>
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
              />
            </div>
          )}

          {phase === 'loading' && (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-500">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              <span className="text-sm font-semibold">KI liest „{fileName}“…</span>
              <span className="text-xs">Kopfdaten, Positionen und Summen werden ausgelesen.</span>
            </div>
          )}

          {phase === 'review' && parsed && (
            <div className="space-y-4">
              {truncated && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    <strong>Unvollständig:</strong> die KI-Antwort wurde abgeschnitten — der Beleg ist länger als das,
                    was hier steht. Bitte vor dem Übernehmen mit dem Original abgleichen.
                  </span>
                </div>
              )}

              {deviationHint && (
                <div className={`flex items-start gap-2 p-3 rounded-xl text-xs border ${deviationHint.missing ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{deviationHint.text} Das Angebot wird trotzdem mit der Summe seiner Positionen angelegt — nie mit der abgelesenen.</span>
                </div>
              )}

              {/* Kunde */}
              <div>
                <label className={labelClass}>Kunde *</label>
                <select className={`${inputClass} mt-1 ${customerId ? '' : 'border-amber-300 bg-amber-50/40'}`}
                        value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                  <option value="">Bitte auswählen …</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  {suggestedName
                    ? <>Im Beleg steht: <strong>{suggestedName}</strong>{customerId ? '' : ' — kein sicherer Treffer im Kundenstamm, bitte selbst zuordnen.'}</>
                    : 'Im Beleg wurde kein Empfänger erkannt.'}
                </p>
              </div>

              {/* Status */}
              <div>
                <label className={labelClass}>Ausgang des Angebots *</label>
                <select className={`${inputClass} mt-1`} value={status} onChange={(e) => setStatus(e.target.value as QuoteStatus)}>
                  {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  {STATUS_OPTIONS.find((o) => o.value === status)?.hint}
                </p>
              </div>

              {(status === 'accepted' || status === 'rejected') && (
                <div>
                  <label className={labelClass}>Datum der Entscheidung</label>
                  <input type="date" className={`${inputClass} mt-1`} value={decisionDate}
                         onChange={(e) => setDecisionDate(e.target.value)} />
                  <p className="text-xs text-slate-500 mt-1">
                    Steht nicht im Beleg, bestimmt aber, in welchem Zeitraum das Angebot als entschieden zählt.
                    Vorbelegt mit dem Angebotsdatum — ohne Angabe landet der Altbeleg sonst im laufenden Monat.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className={labelClass}>Betreff</label>
                  <input className={`${inputClass} mt-1`} value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>Angebotsdatum</label>
                  <input type="date" className={`${inputClass} mt-1`} value={quoteDate} onChange={(e) => setQuoteDate(e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>Gültig bis</label>
                  <input type="date" className={`${inputClass} mt-1`} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>Steuersatz (%)</label>
                  <input type="number" min={0} max={100} step={0.1} className={`${inputClass} mt-1`}
                         value={taxRate} onChange={(e) => setTaxRate(e.target.value === '' ? 0 : Number(e.target.value))} />
                </div>
                <div>
                  <label className={labelClass}>Nachlass (aus Beleg)</label>
                  <input className={`${inputClass} mt-1 bg-slate-50`} value={`${eur(parsed.header.discountPercent)} %`} readOnly />
                </div>
              </div>

              {/* Positionen */}
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                  <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                    {parsed.items.length} Position(en){parsed.sections.length ? ` in ${parsed.sections.length} Abschnitt(en)` : ''}
                  </span>
                  <span className="text-sm font-bold text-slate-800">{eur(parsed.computedNet)} € netto</span>
                </div>
                <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                  {parsed.items.map((it) => (
                    <div key={it.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                      <span className="w-16 shrink-0 text-xs text-slate-400 font-mono">{it.lvPosition || '—'}</span>
                      <span className="flex-1 min-w-0 truncate text-slate-700">
                        {it.title}
                        {it.nep && <span className="ml-2 text-[10px] font-bold uppercase text-amber-600">NEP</span>}
                      </span>
                      <span className="w-24 shrink-0 text-right text-xs text-slate-500">
                        {eur(it.quantity)} {it.unit}
                      </span>
                      <span className="w-24 shrink-0 text-right text-slate-700">{eur(it.unitPrice)} €</span>
                    </div>
                  ))}
                </div>
              </div>

              {parsed.skipped.length > 0 && (
                <div className="flex items-start gap-2 p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-600">
                  <Info className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
                  <span>
                    {parsed.skipped.length} Zeile(n) wurden nicht als Position übernommen (Überschriften, Zwischensummen):{' '}
                    {parsed.skipped.slice(0, 5).map((s) => s.bezeichnung).filter(Boolean).join(' · ')}
                    {parsed.skipped.length > 5 ? ' …' : ''}
                  </span>
                </div>
              )}

              {outerError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{outerError}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Fuss */}
        {phase === 'review' && (
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50">
            <Button variant="ghost" onClick={onClose} className="h-10 text-sm" disabled={busy}>Abbrechen</Button>
            <Button onClick={handleImport} disabled={!canImport} icon={busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} className="h-10 text-sm">
              {busy ? 'Angebot wird angelegt…' : 'Angebot anlegen'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
